import React, { useState, useEffect, useRef } from 'react';

const ChatInterface = ({ messages, sendMessage, characterName, isSending, isPlayModeRef, currentScore, exclusive_actions }) => {
    const [input, setInput] = useState('');
    const [isPlayMode, setIsPlayMode] = useState(false);
    const [isExpand, setIsExpand] = useState(false);
    const messagesEndRef = useRef(null);

    // Automatically scroll to the latest message
    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }, [messages]);

    const handleFocus = () => {
        sendMessage({ type: "USER_TYPING_START" });
    };
    const handleSend = () => {
        if (input.trim() && !isSending) {
            sendMessage({ type: "USER_SEND_MESSAGE", text: input });
            setInput('');
        }
    };

    const handleToggleChange = () => {
        if (isPlayMode) {
            setIsPlayMode(false);
        } else {
            setIsPlayMode(true);
            setTimeout(function () {
                sendMessage({ type: "USER_SEND_MESSAGE", text: "Let's Play" });
            }, 500)
        }
    };

    const toggleExclusiveActionSection = () => {
        if (isExpand){
            setIsExpand(false);
        } else {
            setIsExpand(true);
        }
    }

    // Use useEffect to update the ref whenever the state changes
    useEffect(() => {
        isPlayModeRef.current = isPlayMode;
    }, [isPlayMode]);

    // Formats the timestamp for display
    const formatTimestamp = (timestamp) => {
        if (!timestamp) return ''; // Return empty string if timestamp is not available
        return new Date(timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    };

    const handleActionClick = (id) => {
        console.log(`Action with ID ${id} clicked!`);
        sendMessage({ type: "TRANSITION_ACTION", id: id });
    };

    const handleOptionClick = (option_text) => {
        sendMessage({ type: "USER_SEND_MESSAGE", text: option_text });
    }

    return (
        <div className="chat-interface">
            <div className="chat-header">
                <div className="character-profile">
                    <img src="/images/Missy_AI_Profile_Image.png" width="40" height="40" alt={`${characterName} Profile`} />
                </div>
                <div className="character-information-block">
                    <div className="character-title-header">
                        <span>{characterName}</span>
                    </div>
                    <div className="character-status">
                        <div className="points-status">
                            <span>Points: {currentScore}</span>
                        </div>
                    </div>
                </div>
                <div className={`game-chat-switch-container ${isSending ? 'disabled-toggle':''}`}>
                    <span className="status-text" id="switchLabel">Play Game</span>
                    <label className="game-switch">
                        <input type="checkbox" id="modeToggle"
                            checked={isPlayMode}
                            onChange={handleToggleChange}
                            disabled={isSending}
                        ></input>
                        <span className="slider"></span>
                    </label>
                </div>
            </div>
            <div className="chat-messages">
                {messages.map((msg, index) => (
                    <div key={index} className={`message-bubble-wrapper ${msg.from === 'user' ? 'user-wrapper' : 'ai-wrapper'}`}>
                        <div className={`message-bubble ${msg.from === 'user' ? 'user-bubble' : 'ai-bubble'}`}>
                            
                            {/* 1. USER MESSAGE RENDERER */}
                            {msg.from === 'user' && <span>{msg.text}</span>}

                            {/* 2. AI MESSAGE RENDERER */}
                            {msg.from !== 'user' && (
                                <>
                                    {msg.type === "qna" ? (
                                        <div className="qna-container">
                                            <div className="commentary">{msg.commentary}</div>
                                            {(msg.question && msg.options) && 
                                                <div className="qna_section">
                                                    <h3>Question:</h3>
                                                    <label>
                                                        {msg.question}
                                                    </label>
                                                    <div className="options-list">
                                                        {msg.options.map((option, optIndex) => (
                                                            <button
                                                                key={optIndex}
                                                                className="qna_option"
                                                                onClick={() => handleOptionClick(option.text)}
                                                            >
                                                                {option.text}
                                                            </button>
                                                        ))}
                                                    </div>
                                                </div>
                                            }
                                        </div>
                                    ) : (
                                        <span>{msg.text}</span>
                                    )}
                                </>
                            )}
                        </div>

                        {/* 3. TIMESTAMP & STATUS */}
                        <div className="message-timestamp">
                            {msg.showWaitingMessage ? (
                                <>
                                    <img src="/images/timer.png" alt="sending" />
                                    <span>sending....</span>
                                </>
                            ) : (
                                <>
                                    <img src="/images/sent.png" alt="sent" />
                                    <span>{formatTimestamp(msg.timestamp)}</span>
                                </>
                            )}
                        </div>
                    </div>
                ))}
                <div ref={messagesEndRef} />
            </div>
            <div className="chat-input-area">
                <input
                    type="text"
                    className="chat-input"
                    value={input}
                    placeholder={`Message ${characterName}...`}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleSend()}
                    onFocus={handleFocus}
                    disabled={isSending}
                />
                <button className="send-button" onClick={handleSend} disabled={isSending}>
                    <img src="https://cdn-icons-png.flaticon.com/512/10322/10322482.png" width="20" height="20" alt="" title="Send Message" className="img-small"></img>
                </button>
            </div>
            <div className={`unlock-actions-container ${isExpand ? 'active' : ''}`}>
                <div className="unlock-actions-header" onClick={toggleExclusiveActionSection}>
                    <span className="unlock-section-title">Unlock Exclusive Actions</span>
                    <span className="unlock-actions-expand-button">
                        <img width="15px" height="15px" src="/images/chevron.png"></img>
                    </span>
                </div>
                <div className={`unlock-actions-body ${isSending ? 'disabled-toggle':''}`}>
                    {exclusive_actions.map((action) => (
                        <div
                            key={action.id}
                            className="unlock-actions-list"
                            onClick={() => handleActionClick(action.id)}
                        >
                            <span className="unlock-actions-list-title">
                                {action.title}
                            </span>
                            
                            <span className="unlock-actions-list-points points-status">
                                Points: {action.points}
                            </span>

                            {action.lock && (
                            <img
                                src="https://cdn-icons-png.flaticon.com/512/2886/2886699.png"
                                width="15px"
                                height="15px"
                                alt="locked"
                            />
                            )}
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

export default ChatInterface;